import { Grid, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import FormControl from '@material-ui/core/FormControl'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import TextField from '@material-ui/core/TextField'
import ToggleOffIcon from '@material-ui/icons/ToggleOffOutlined'
import ToggleOnIcon from '@material-ui/icons/ToggleOnOutlined'
import { Stack, Switch, FormControlLabel as FormControlLabel5, IconButton } from '@mui/material'
import _, { toUpper, upperFirst, isEmpty } from 'lodash'
import React, { ChangeEvent, useEffect, useMemo, useState } from 'react'
import { renderToStaticMarkup } from 'react-dom/server'
import { useTranslation } from 'react-i18next'
import { IconBaseProps } from 'react-icons'

import {
  Policy_KindEnum,
  PolicyRule_VehicleTypesEnum,
  Provider,
  AreaLabelResponse,
  Policy_FeeCalculationMethodEnum,
  Policy_MaintenanceKindsEnum,
  PolicyRule_RuleUnitsEnum,
  Policy_PenaltyFrequencyEnum,
} from 'src/api/gen'
import { IPreviousPolicy } from 'src/api/types/policy'
import { CurrencyKind } from 'src/api/types/units'
import { getEnumEntries } from 'src/commons/filters/utils'
import { getTranslation } from 'src/commons/i18n'
import { enumsTranslationKeys, eventTypeTranslationKey } from 'src/commons/i18nEnums'
import { areDictEquals } from 'src/commons/policies/utils'
import { MultipleAutocomplete } from 'src/components/Autocomplete'
import { IDateTimeRange, DateRangePicker } from 'src/components/DatePickers'
import { ConfirmationDialog, useDialog } from 'src/components/Dialog'
import { Field, IFieldProps } from 'src/components/fields'
import NumericInput from 'src/components/Input/NumericInput'
import { MultiSelectChips, SelectBox } from 'src/components/Select'
import { useAgencySettings } from 'src/layout/AgencySettings'

import { Validation } from '../../../commons/policies/validation'
import { FieldTitle } from '../commons'

interface INameProps extends IFieldProps {
  handleChange: (value: string) => void
  validation: Validation
  value: string
}

export const NameField = (props: INameProps) => {
  const { value, handleChange, validation, disabled } = props
  return (
    <Field {...props}>
      <TextField
        inputProps={{ 'data-testid': 'name-field' }}
        disabled={disabled}
        variant="outlined"
        margin="dense"
        value={value}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value)}
        error={!validation.isFieldValid('name')}
        helperText={validation.getFieldErrorMsg('name')}
      />
    </Field>
  )
}

interface IKindProps extends IFieldProps {
  data: { id: string; label: string }[]
  handleChange: (value: any) => void
  validation: Validation
  value: Policy_KindEnum | undefined
}

export const KindField = (props: IKindProps) => {
  const { value, handleChange, validation, disabled, data } = props

  return (
    <Field {...props}>
      <SelectBox
        inputProps={{ 'data-testid': 'kind-field' }}
        disabled={disabled}
        data={data}
        onChange={handleChange}
        value={value}
        error={!validation.isFieldValid('kind')}
        helperText={validation.getFieldErrorMsg('kind')}
      />
    </Field>
  )
}

interface IFeeCalculationMethodProps extends IFieldProps {
  handleChange: (value: any) => void
  value: Policy_FeeCalculationMethodEnum
}

export const FeeCalculationMethodField = (props: IFeeCalculationMethodProps) => {
  const { t } = getTranslation()
  const { value, handleChange, disabled } = props

  return (
    <Field {...props}>
      <SelectBox
        disabled={disabled}
        data={Object.entries(enumsTranslationKeys.FeeCalculationMethod).map(([id, label]) => ({
          id,
          label: t(label),
        }))}
        onChange={handleChange}
        value={value}
      />
    </Field>
  )
}

interface IProviderProps extends IFieldProps {
  // List of IDs
  handleChange: (value: any) => void
  providers?: Provider[] | undefined
  validation: Validation
  value?: string[] | undefined
}

const buildItemsFromProviders = (providers?: Provider[] | undefined) =>
  (providers || []).map((provider) => ({
    id: provider.id,
    label: provider.name,
  }))

export const ProviderField = (props: IProviderProps) => {
  const { value, handleChange, validation, disabled, providers } = props

  return (
    <Field {...props}>
      <div style={{ height: 8 }} />
      <MultiSelectChips
        disabled={disabled}
        data={buildItemsFromProviders(providers)}
        oneLineSelect={true}
        onMultiChange={handleChange}
        value={value || []}
        error={!validation.isFieldValid('providers')}
        helperText={validation.getFieldErrorMsg('providers')}
        withSelectAll={true}
        inputProps={{ 'data-testid': 'provider-field' }}
      />
    </Field>
  )
}

interface IPeriodProps extends IFieldProps {
  end_date: Temporal.ZonedDateTime | undefined
  handleChange: (value: IDateTimeRange) => void
  minDate?: Temporal.ZonedDateTime | undefined
  startDisabled?: boolean
  start_date: Temporal.ZonedDateTime | undefined
  sx: { [key: string]: number } | undefined
  validation: Validation
}

export const PeriodField = (props: IPeriodProps) => {
  const { start_date, end_date, handleChange, validation, disabled, startDisabled } = props

  return (
    <Grid container={true} id={props.id}>
      <Grid item={true} xs={12}>
        {props.title && <FieldTitle title={props.title} tooltip={props.tooltip} />}
      </Grid>
      <Grid item={true} xs={12}>
        <DateRangePicker
          disabled={disabled}
          startDisabled={startDisabled}
          value={{ from: start_date, to: end_date }}
          minDate={props.minDate}
          onChange={handleChange}
          clearable={true}
          withTime={true}
          error={{
            from: !validation.isFieldValid('start_date'),
            to: !validation.isFieldValid('end_date'),
          }}
          helperText={<div> {validation.getFieldErrorMsg('start_date')} </div>}
          sx={props.sx}
        />
      </Grid>
    </Grid>
  )
}

interface IDescriptionProps extends IFieldProps {
  handleChange: (value: string) => void
  value: string | undefined
}

export const DescriptionField = (props: IDescriptionProps) => {
  const { value, handleChange, disabled } = props

  return (
    <Field {...props} id={'policy_description'}>
      <TextField
        disabled={disabled}
        variant="outlined"
        margin="dense"
        value={value}
        multiline={true}
        minRows={5}
        maxRows={5}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleChange(e.target.value)}
        inputProps={{ 'data-testid': 'description-field' }}
      />
    </Field>
  )
}

export interface IIntegerProps extends IFieldProps {
  handleChange: (value: number) => void
  maximum?: number | undefined
  minimum?: number | undefined
  value: number
}

export const IntegerField = (props: IIntegerProps) => {
  const { value, handleChange, disabled, inputProps } = props

  return (
    <Field {...props}>
      <NumericInput
        disabled={disabled}
        value={value}
        onChange={handleChange}
        min={props.minimum}
        max={props.maximum}
        inputProps={inputProps}
      />
    </Field>
  )
}

interface ICurrencyProps extends IIntegerProps {
  currency?: string
  id?: string
  kind?: CurrencyKind
}

export const CurrencyIntegerField = (props: ICurrencyProps) => {
  const { value, handleChange, disabled, kind, inputProps } = props
  const agencySettings = useAgencySettings()
  let currency: string

  if (props.currency) {
    currency = props.currency
  } else {
    if (kind === CurrencyKind.Permit) {
      currency = agencySettings.permit_currency_symbol
    } else {
      currency = agencySettings.penalty_symbol
    }
  }

  return (
    <Field {...props}>
      <NumericInput
        disabled={disabled}
        currency={currency}
        value={value}
        onChange={handleChange}
        inputProps={inputProps}
      />
    </Field>
  )
}

interface IDwellTimePenaltyToggleProps {
  disabled: boolean
  penalty_frequency?: Policy_PenaltyFrequencyEnum
  togglePenaltyFrequency: () => void
}

export const DwellTimePenaltyToggle = (props: IDwellTimePenaltyToggleProps) => {
  return (
    <IconButton sx={{ height: 'fit-content' }} onClick={props.togglePenaltyFrequency} disabled={props.disabled}>
      {props.penalty_frequency === Policy_PenaltyFrequencyEnum.hourly ? <ToggleOnIcon /> : <ToggleOffIcon />}
    </IconButton>
  )
}

interface IDwellTimePenaltyProps {
  disabled: boolean
  handleChange: (value: number) => void
  penalty_frequency?: Policy_PenaltyFrequencyEnum
  title: string
  togglePenaltyFrequency: () => void
  value: number
}

export const DwellTimePenalty = (props: IDwellTimePenaltyProps) => {
  return (
    <Stack flexDirection="row" alignItems="center">
      <CurrencyIntegerField
        id="dwell_time_hourly_penalty"
        disabled={props.disabled}
        title={props.title}
        value={props.value}
        handleChange={props.handleChange}
      />
      <DwellTimePenaltyToggle
        togglePenaltyFrequency={props.togglePenaltyFrequency}
        disabled={props.disabled}
        penalty_frequency={props.penalty_frequency}
      />
    </Stack>
  )
}

interface IRuleFieldProps {
  disabled?: boolean
  handleChange: (value: any) => void
}

interface INumberField extends IRuleFieldProps {
  error?: boolean
  helperText?: string | null | undefined
  inputType?: 'outlined' | 'text'
  value: number
}

export const NumberField = (props: INumberField) => {
  return <NumericInput value={props.value} onChange={props.handleChange} inputType={props.inputType} />
}

interface IRuleNameProps extends IRuleFieldProps {
  error?: boolean
  helperText?: string | null | undefined
  id?: string
  value: string
}

export const RuleNameField = (props: IRuleNameProps) => {
  const { value, handleChange, disabled, error, helperText } = props
  /* Buffer trick taken from NumericInput
   * Piles the characters and them at once when the user moves to another
   * input/component (onBlur).
   */
  const [bufferValue, setBufferValue] = useState((value || 0).toString())
  useEffect(() => {
    setBufferValue(value || '')
  }, [value])

  const setValue = () => {
    handleChange(bufferValue)
    setBufferValue(bufferValue)
  }
  // Pretty neat, eh?

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} id={props.id}>
      <TextField
        variant="outlined"
        margin="dense"
        fullWidth={true}
        value={bufferValue}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setBufferValue(e.target.value)}
        onBlur={setValue}
        disabled={disabled}
        error={error}
        helperText={helperText}
      />
    </div>
  )
}

interface IGeographiesProps extends IRuleFieldProps {
  allGeographies: AreaLabelResponse[]
  error?: boolean
  helperText: React.ReactNode
  id?: string
  value: string[]
}

export const GeographiesField = (props: IGeographiesProps) => {
  const { value, handleChange, allGeographies, disabled, error, helperText } = props

  return (
    <MultiSelectChips
      id={props.id}
      disabled={disabled}
      oneLineSelect={true}
      data={allGeographies}
      onMultiChange={handleChange}
      value={value}
      error={error}
      helperText={helperText}
      withSelectAll={true}
    />
  )
}

interface IGeographyProps extends IRuleFieldProps {
  allGeographies: AreaLabelResponse[]
  error?: boolean
  helperText?: React.ReactNode
  id?: string
  value: string
}

export const GeographyField = (props: IGeographyProps) => {
  const { value, handleChange, allGeographies, disabled, error, helperText } = props

  return (
    <SelectBox
      id={props.id}
      disabled={disabled}
      data={allGeographies}
      onChange={handleChange}
      value={value}
      error={error}
      helperText={helperText}
    />
  )
}

interface IVehicleTypesProps {
  disabled?: boolean
  error?: boolean
  handleChange: (value: any) => void
  helperText: React.ReactNode
  id?: string
  title?: string
  value: PolicyRule_VehicleTypesEnum[]
}

export const VehicleTypesField = (props: IVehicleTypesProps) => {
  const { vehicle_types } = useAgencySettings()
  const { value, handleChange, disabled, error, helperText } = props

  return (
    <>
      {props.title ? <FieldTitle title={props.title} /> : null}
      <MultiSelectChips
        id={props.id}
        disabled={disabled}
        oneLineSelect={true}
        data={getEnumEntries(vehicle_types, enumsTranslationKeys.DeviceType, undefined, false)}
        onMultiChange={handleChange}
        value={value}
        error={error}
        helperText={helperText}
        withSelectAll={true}
      />
    </>
  )
}

interface IVehicleCategoriesProps {
  disabled?: boolean
  error?: boolean
  handleChange: (value: any) => void
  helperText: React.ReactNode
  value: string[]
}

// This component was made to simplify the creation of Beginner policy rules by directly offer the choice
// of vehicle categories with the mapping set in the back.
// However I'm still not sure about its relevancy because we still use "vehicle_types" everywhere else
// and the code is also not very clear.
export function VehicleCategoriesField(props: IVehicleCategoriesProps) {
  const { vehicle_types, device_type_to_category_mapping } = useAgencySettings()

  // Kinda looks like a trick to get the categories for a specific device type
  const vehicleCategories = new Set(
    vehicle_types.map((vehicle_type) => device_type_to_category_mapping[vehicle_type as any])
  )
  const displayedValue = props.value.map((subValue) => device_type_to_category_mapping[subValue as any])

  return (
    <MultiSelectChips
      disabled={props.disabled}
      oneLineSelect={true}
      data={getEnumEntries([...vehicleCategories], enumsTranslationKeys.DeviceCategory, undefined, false)}
      onMultiChange={props.handleChange}
      value={displayedValue}
      error={props.error}
      helperText={props.helperText}
      withSelectAll={true}
    />
  )
}

interface IPreviousPoliciesProps {
  allPrevPolicies: IPreviousPolicy[] | undefined
  disabled: boolean
  handleChange: (value: any) => void
  id?: string
  title: string
  value: string[] | undefined
}

export function PreviousPoliciesField(props: IPreviousPoliciesProps) {
  const [currentValues, setCurrentValues] = useState<IPreviousPolicy[]>([])

  useEffect(() => {
    const prevPoliciesIds: string[] = props.allPrevPolicies?.map((prevPolicy) => prevPolicy.id) ?? []
    if (props.allPrevPolicies && prevPoliciesIds && props.value) {
      if (
        props.value &&
        prevPoliciesIds.filter((prevPolicyId) => props.value && props.value.includes(prevPolicyId)).length === 0
      ) {
        // TODO(xfernandez, 2023-03-16): throw a warning here : we are updating a policy which has now a
        // missing prev_policy current value work out how we want to render this with an AMOA
        console.warn('Custom WARNING + cleanup')
      } else {
        setCurrentValues(
          props.allPrevPolicies.filter((prevPolicy) => props.value && props.value.includes(prevPolicy.id))
        )
      }
    }
  }, [props.allPrevPolicies, props.value])

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} id={props.id}>
      <FieldTitle title={props.title} />
      <div style={{ height: 8 }} />
      <MultipleAutocomplete
        options={props.allPrevPolicies}
        value={currentValues}
        handleChange={props.handleChange}
        disabled={props.disabled}
        inputProps={{ 'data-testid': 'previous-policies-field' }}
      />
    </div>
  )
}

interface IVehicleEventStatesSelectorProps {
  disabled: boolean
  handleChange: (vehicle_events_and_states: any) => void
  id?: string
  isRegistered: boolean
  policy_kind: Policy_KindEnum
  policy_vehicle_events_and_states?: { [key: string]: Array<string> }
}

export const useVehicleEventStatesSelector = (
  policy_kind: Policy_KindEnum,
  isRegistered: boolean,
  policy_vehicle_events_and_states?: { [key: string]: Array<string> }
) => {
  const agencySettings = useAgencySettings()
  const vehicle_state_events = agencySettings.vehicle_state_events?.[policy_kind] ?? {}

  const [events, setEvents] = useState<{ [key: string]: Array<string> }>(policy_vehicle_events_and_states ?? {})
  useEffect(() => {
    if (!isRegistered) {
      selectAll()
    }
  }, [policy_kind])

  const updateEvents = (state: string, event: string) => {
    const newEvents = { ...events }
    if (!(state in newEvents)) {
      newEvents[state] = []
    }

    const eventIndex = newEvents[state].indexOf(event)
    if (eventIndex === -1) {
      newEvents[state].push(event)
    } else {
      newEvents[state].splice(eventIndex, 1)
    }
    setEvents(newEvents)
  }

  const selectAll = () => {
    const newEvents = {} as { [key: string]: Array<string> }
    Object.entries(vehicle_state_events).forEach(([state, event_types]) => {
      newEvents[state] = event_types.map((event) => event)
    })
    setEvents(newEvents)
  }

  const deselectAll = () => {
    setEvents({})
  }

  const empty = isEmpty(vehicle_state_events)

  return { empty, vehicle_state_events, events, updateEvents, selectAll, deselectAll }
}

export const VehicleEventStatesSelector = (props: IVehicleEventStatesSelectorProps) => {
  const { t } = getTranslation()
  const { empty, vehicle_state_events, events, updateEvents, selectAll, deselectAll } = useVehicleEventStatesSelector(
    props.policy_kind,
    props.isRegistered,
    props.policy_vehicle_events_and_states
  )
  const { isOpen, open, close, handleClose } = useDialog({
    onClose: () => {
      props.handleChange(events)
    },
  })
  const {
    isOpen: confirmIsOpen,
    open: openConfirm,
    close: closeConfirm,
    handleClose: handleConfirmClose,
  } = useDialog({
    onClose: handleClose,
  })

  if (empty) {
    return null
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <FieldTitle title={upperFirst(t('titles.configure-events'))} tooltip={t('tooltips.configure-events')} />
        <Button
          id={props.id}
          variant="outlined"
          onClick={open}
          style={{ width: 'fit-content', marginTop: '8px' }}
          size="large"
        >
          {t<string>('buttons.configure-events.open')}
        </Button>
      </div>
      <Dialog open={isOpen} maxWidth={false}>
        <DialogContent>
          {Object.entries(vehicle_state_events).map(([state, event_types]) => (
            <FormControl component="fieldset" variant="standard" key={state}>
              <FormLabel>
                <Typography variant="h6" color="textPrimary">
                  {toUpper(t(enumsTranslationKeys.VehicleStates[state]))}
                </Typography>
              </FormLabel>
              <FormGroup key={state}>
                {event_types.map((event) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={events[state]?.includes(event) ?? false}
                        onChange={() => updateEvents(state, event)}
                        color="primary"
                        disabled={props.disabled}
                      ></Checkbox>
                    }
                    label={upperFirst(t(eventTypeTranslationKey(event)))}
                    key={event}
                  />
                ))}
              </FormGroup>
            </FormControl>
          ))}
        </DialogContent>
        <DialogActions>
          {props.disabled ? (
            <Button variant="contained" color="primary" onClick={close}>
              {t<string>('buttons.configure-events.close')}
            </Button>
          ) : (
            <>
              <Button variant="contained" color="primary" onClick={selectAll}>
                {t<string>('buttons.configure-events.select-all')}
              </Button>
              <Button variant="contained" color="primary" onClick={deselectAll}>
                {t<string>('buttons.configure-events.deselect-all')}
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  if (!areDictEquals(events, vehicle_state_events)) {
                    openConfirm()
                  } else {
                    handleClose()
                  }
                }}
                disabled={isEmpty(events)}
              >
                {t<string>('buttons.configure-events.save')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={confirmIsOpen}
        action="confirm"
        onClose={closeConfirm}
        onConfirmation={handleConfirmClose}
        descriptionMessage={t('descriptions.configure-events.confirm')}
      />
    </>
  )
}

interface ISwitchOption<ValueType> {
  icon?: (props: IconBaseProps) => React.ReactElement
  label?: string
  value: ValueType
}

interface ISwitchField<ValueType> extends IRuleFieldProps {
  options: [ISwitchOption<ValueType>, ISwitchOption<ValueType>]
  value: ValueType
}

const convertSvg = (svg: React.ReactElement | undefined) => {
  if (svg == undefined) {
    return undefined
  }

  const markup = renderToStaticMarkup(svg)
  const encoded = encodeURIComponent(markup)
  const dataUri = `url('data:image/svg+xml;utf8,${encoded}')`
  return dataUri
}

export const SwitchField = function <ValueType>(props: ISwitchField<ValueType>) {
  const onChange = function (_event: unknown, checked: boolean) {
    if (checked) {
      props.handleChange(props.options[1].value)
    } else {
      props.handleChange(props.options[0].value)
    }
  }

  return (
    <FormControlLabel5
      sx={{ marginX: 0, paddingBottom: '0.3em' }}
      control={
        <>
          <Typography component={'span'}>{props.options[0].label}</Typography>
          <Switch
            checked={props.value === props.options[1].value}
            onChange={onChange}
            disabled={props.disabled}
            sx={(theme) => ({
              // Inspired by https://codesandbox.io/s/mui-switch-with-icons-yg3hct?file=/demo.tsx:883-894
              width: '80px',
              height: '2.9em',
              '& .MuiSwitch-thumb': {
                width: '2.6em',
                height: '2.6em',
                backgroundColor: theme.palette.primary.main,
                opacity: props.disabled ? 0.5 : undefined,
              },
              alignItems: 'center',
              '& .MuiSwitch-track': {
                height: 'inherit',
                borderRadius: '25px',
                border: `1px solid grey`,
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                '&:before, &:after': {
                  content: '""',
                  width: 32,
                  height: '100%',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'contain',
                  opacity: 0.6,
                },
                '&:before': {
                  backgroundImage: convertSvg(props.options[0].icon?.({})),
                },
                '&:after': {
                  backgroundImage: convertSvg(props.options[1].icon?.({})),
                },
              },
              '& .MuiSwitch-thumb:before': {
                content: "''",
                display: 'block',
                width: '100%',
                height: '100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
                backgroundImage: convertSvg(props.options[0].icon?.({ color: 'white' })),
              },
              '& .MuiSwitch-switchBase': {
                bottom: '1px',
                left: '6px',
                '&.Mui-checked': {
                  transform: 'translateX(24px)',
                  '& + .MuiSwitch-track': {
                    backgroundColor: 'white',
                  },
                  '& .MuiSwitch-thumb:before': {
                    backgroundImage: convertSvg(props.options[1].icon?.({ color: 'white' })),
                  },
                },
              },
            })}
          />
        </>
      }
      label={props.options[1].label}
    />
  )
}

export const useMaintenanceKindsField = (
  allowed_values: Policy_MaintenanceKindsEnum[],
  handleChange: (value: any) => void,
  kinds: Policy_MaintenanceKindsEnum[]
) => {
  const updateKinds = (kind: Policy_MaintenanceKindsEnum, value: boolean) => {
    if (value && !kinds.includes(kind)) {
      handleChange([...kinds, kind])
    } else {
      handleChange(kinds.filter((elem) => elem !== kind))
    }
  }

  const selectAll = () => {
    handleChange(allowed_values)
  }

  const deselectAll = () => {
    handleChange([])
  }

  useEffect(() => {
    selectAll()
  }, [allowed_values])

  return { updateKinds, selectAll, deselectAll }
}

export const compute_allowed_kinds = (units: PolicyRule_RuleUnitsEnum[]) => {
  const result: Set<Policy_MaintenanceKindsEnum> = new Set()
  units.forEach((unit) => {
    if (unit === PolicyRule_RuleUnitsEnum.days) {
      result.add(Policy_MaintenanceKindsEnum.on_street)
    }
    result.add(Policy_MaintenanceKindsEnum.warehouse)
  })
  return Array.from(result)
}

interface IMaintenanceKindsFieldProps extends IRuleFieldProps {
  rule_units: PolicyRule_RuleUnitsEnum[]
  value: Policy_MaintenanceKindsEnum[]
}

export const MaintenanceKindsField = function (props: IMaintenanceKindsFieldProps) {
  const { t } = useTranslation()

  const allowed_kinds = useMemo(() => compute_allowed_kinds(props.rule_units), props.rule_units)

  const { updateKinds, selectAll, deselectAll } = useMaintenanceKindsField(
    allowed_kinds,
    props.handleChange,
    props.value
  )

  const { isOpen, open, close, handleClose } = useDialog()

  return (
    <>
      <Stack flexDirection="column">
        {allowed_kinds.length > 1 && (
          <Button variant="outlined" onClick={open} style={{ width: 'fit-content', marginTop: '8px' }} size="large">
            {t('buttons.configure-maintenance-kinds.open')}
          </Button>
        )}
      </Stack>
      <Dialog open={isOpen} maxWidth={false}>
        <DialogContent>
          <FormControl component="fieldset" variant="standard">
            <FormGroup>
              {allowed_kinds.map((elem) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={props.value.includes(elem)}
                      onChange={(_, checked) => updateKinds(elem, checked)}
                      color={'primary'}
                      disabled={props.disabled}
                    ></Checkbox>
                  }
                  label={upperFirst(t(enumsTranslationKeys.MaintenanceKinds[elem]))}
                  key={elem.toString()}
                />
              ))}
            </FormGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          {props.disabled ? (
            <Button variant="contained" color="primary" onClick={close}>
              {t('buttons.configure-events.close')}
            </Button>
          ) : (
            <>
              <Button variant="contained" color="primary" onClick={selectAll}>
                {t('buttons.configure-events.select-all')}
              </Button>
              <Button variant="contained" color="primary" onClick={deselectAll}>
                {t('buttons.configure-events.deselect-all')}
              </Button>
              <Button variant="contained" color="primary" onClick={handleClose} disabled={isEmpty(props.value)}>
                {t('buttons.configure-events.save')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  )
}
